import { useUploadOptional } from '@/shared/contexts/UploadContext';
import { Loader } from "@/shared/components/ui/loader";
import { Progress } from '@/shared/components/ui/progress';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import {
    Upload,
    X,
    ChevronDown,
    ChevronUp,
    CheckCircle,
    AlertCircle,
    MinusCircle
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { ExcelService } from '@/features/excel';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export function UploadIndicator() {
    const { t } = useTranslation("shared");
    const uploadContext = useUploadOptional();
    const location = useLocation();

    if (!uploadContext) return null;

    const { uploads, isMinimized, setIsMinimized, clearUpload, clearAllCompleted } = uploadContext;

    // Only show if there are uploads
    if (uploads.length === 0) return null;

    // Don't show if user is on the upload tab - they can see progress in FileUploader
    const isOnUploadTab = location.pathname.includes('/project/') &&
        location.search.includes('tab=upload');
    if (isOnUploadTab) return null;

    const activeUploads = uploads.filter(u =>
        u.status === 'uploading' || u.status === 'processing' || u.status === 'annotating' || u.status === 'pending'
    );
    const completedUploads = uploads.filter(u => u.status === 'success' || u.status === 'error');

    const hasActive = activeUploads.length > 0;

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'error':
                return <AlertCircle className="h-4 w-4 text-red-500" />;
            case 'uploading':
            case 'processing':
            case 'annotating':
                return <Loader size="sm" />;
            default:
                return <Upload className="h-4 w-4 text-muted-foreground" />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending': return t('upload.waiting');
            case 'uploading': return t('upload.sending');
            case 'processing': return t('upload.processing');
            case 'annotating': return t('upload.annotating');
            case 'success': return t('upload.completed');
            case 'error': return t('upload.error');
            default: return status;
        }
    };

    return (
        <Card className={cn(
            "fixed bottom-4 right-4 z-50 shadow-lg transition-all duration-300",
            isMinimized ? "w-auto" : "w-80"
        )}>
            {/* Header */}
            <div
                className="flex items-center justify-between p-3 bg-visagio-teal text-white rounded-t-lg cursor-pointer"
                onClick={() => setIsMinimized(!isMinimized)}
            >
                <div className="flex items-center gap-2">
                    {hasActive ? (
                        <Loader size="sm" />
                    ) : (
                        <Upload className="h-4 w-4" />
                    )}
                    <span className="font-medium text-sm">
                        {t('upload.uploads')} {hasActive && `(${activeUploads.length} ${t('upload.active', { count: activeUploads.length })})`}
                    </span>
                </div>
                <div className="flex items-center gap-1">
                    {completedUploads.length > 0 && !isMinimized && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-white/80 hover:text-white hover:bg-white/20"
                            onClick={(e) => {
                                e.stopPropagation();
                                clearAllCompleted();
                            }}
                        >
                            <MinusCircle className="h-3 w-3" />
                        </Button>
                    )}
                    {isMinimized ? (
                        <ChevronUp className="h-4 w-4" />
                    ) : (
                        <ChevronDown className="h-4 w-4" />
                    )}
                </div>
            </div>

            {/* Content */}
            {!isMinimized && (
                <div className="max-h-64 overflow-y-auto">
                    {uploads.map(upload => (
                        <div key={upload.id} className="p-3 border-b last:border-b-0">
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                    {getStatusIcon(upload.status)}
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium truncate" title={upload.fileName}>
                                            {upload.fileName}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {ExcelService.formatBytes(upload.fileSize)} • {getStatusText(upload.status)}
                                        </p>
                                    </div>
                                </div>
                                {(upload.status === 'success' || upload.status === 'error') && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 shrink-0"
                                        onClick={() => clearUpload(upload.id)}
                                    >
                                        <X className="h-3 w-3" />
                                    </Button>
                                )}
                            </div>

                            {/* Progress bar for active uploads */}
                            {(upload.status === 'uploading' || upload.status === 'processing' || upload.status === 'annotating') && (
                                <div className="mt-2">
                                    <Progress value={upload.progress} className="h-1.5" />
                                    <p className="text-xs text-muted-foreground mt-1 truncate">
                                        {upload.message}
                                    </p>
                                </div>
                            )}

                            {/* Error message */}
                            {upload.status === 'error' && upload.error && (
                                <p className="text-xs text-red-500 mt-1 truncate" title={upload.error}>
                                    {upload.error}
                                </p>
                            )}

                            {/* Success result */}
                            {upload.status === 'success' && upload.result?.table_name && (
                                <p className="text-xs text-green-600 mt-1">
                                    {t('upload.table', { name: upload.result.table_name })}
                                    {upload.result.row_count && ` (${t('upload.rowCount', { count: upload.result.row_count })})`}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </Card>
    );
}

export default UploadIndicator;
